import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Sessao } from "@base/contracts";
import { ErroDaApi } from "../http/erro-da-api";
import type { AuthGateway } from "./auth.gateway";
import type { ControleDeToken } from "./token";

type EstadoDeAuth = {
  readonly sessao: Sessao | null;
  readonly carregando: boolean;
  readonly erro: string | null;
  readonly tokenAtual: () => string | null;
  readonly entrar: (
    email: string,
    senha: string,
    opcoes?: { readonly manterConectado?: boolean },
  ) => Promise<void>;
  readonly sair: () => Promise<void>;
  readonly trocarTenant: (tenantId: string) => Promise<void>;
  /** Há token guardado, mas o servidor não respondeu: dá para tentar de novo. */
  readonly semConexao: boolean;
  readonly tentarDeNovo: () => Promise<void>;
};

/** Esperas entre tentativas quando a API não responde (deploy, rede). */
const ESPERAS_PADRAO_MS: readonly number[] = [1_000, 2_000, 4_000];

/**
 * Quem marcou "Manter conectado" ganha um token novo (e +7 dias no servidor)
 * uma vez por dia de uso. Sem isso a sessão vencia em 7 dias corridos mesmo
 * com a pessoa usando o app todo dia.
 */
export const RENOVAR_SESSAO_APOS_MS = 24 * 60 * 60 * 1_000;

/** Fora do componente: referência estável, senão o efeito de sessão roda em laço. */
const AGORA_PADRAO = (): number => Date.now();

function esperar(ms: number): Promise<void> {
  return new Promise((resolver) => setTimeout(resolver, ms));
}

const ContextoDeAuth = createContext<EstadoDeAuth | null>(null);

/**
 * Orquestra login/logout: e-mail/senha na API e guarda o token de sessao.
 */
export function ProvedorDeAuth({
  gateway,
  tokens,
  children,
  esperasMs = ESPERAS_PADRAO_MS,
  agora = AGORA_PADRAO,
}: {
  readonly gateway: AuthGateway;
  readonly tokens: ControleDeToken;
  readonly children: ReactNode;
  readonly esperasMs?: readonly number[];
  readonly agora?: () => number;
}) {
  const [sessao, definirSessao] = useState<Sessao | null>(null);
  const [carregando, definirCarregando] = useState(
    () => tokens.tokenAtual() !== null,
  );
  const [erro, definirErro] = useState<string | null>(null);
  const [semConexao, definirSemConexao] = useState(false);

  const renovarSeManterConectado = useCallback(async () => {
    if (!tokens.manterConectadoAtual()) return;
    const gravadoEm = tokens.gravadoEm();
    if (gravadoEm !== null && agora() - gravadoEm < RENOVAR_SESSAO_APOS_MS) {
      return;
    }
    try {
      const { token } = await gateway.refresh();
      tokens.definirToken(token, { manterConectado: true });
    } catch {
      // Sem renovar hoje: o token atual continua valendo. Tenta na próxima.
    }
  }, [agora, gateway, tokens]);

  const carregarSessao = useCallback(async () => {
    if (!tokens.tokenAtual()) {
      definirSessao(null);
      definirCarregando(false);
      return;
    }
    definirCarregando(true);
    definirSemConexao(false);
    for (let tentativa = 0; ; tentativa += 1) {
      try {
        const atual = await gateway.obterSessao();
        definirSessao(atual);
        definirErro(null);
        definirCarregando(false);
        void renovarSeManterConectado();
        return;
      } catch (causa) {
        // Só 401 (sessão inválida/vencida) desloga. Deploy, API reiniciando
        // ou rede caindo não podem apagar o "Manter conectado".
        if (causa instanceof ErroDaApi && causa.ehNaoAutorizado) {
          tokens.definirToken(null);
          definirSessao(null);
          definirErro(null);
          definirCarregando(false);
          return;
        }
        const repetivel = !(causa instanceof ErroDaApi) || causa.valeRepetir;
        const espera = esperasMs[tentativa];
        if (repetivel && espera !== undefined) {
          await esperar(espera);
          continue;
        }
        definirSessao(null);
        definirSemConexao(true);
        definirErro(
          causa instanceof Error ? causa.message : "Falha ao validar a sessão.",
        );
        definirCarregando(false);
        return;
      }
    }
  }, [esperasMs, gateway, renovarSeManterConectado, tokens]);

  // Valida a sessão uma vez, ao montar. Depender de `carregarSessao` aqui
  // reexecutava o efeito a cada render (laço de "Validando acesso…").
  const carregarSessaoRef = useRef(carregarSessao);
  carregarSessaoRef.current = carregarSessao;
  useEffect(() => {
    void carregarSessaoRef.current();
  }, []);

  const entrar = useCallback(
    async (
      email: string,
      senha: string,
      opcoes?: { readonly manterConectado?: boolean },
    ) => {
      const emailLimpo = email.trim().toLowerCase();
      if (!emailLimpo.includes("@")) {
        throw new Error("Informe um e-mail válido.");
      }
      if (senha.length < 8) {
        throw new Error("A senha precisa ter pelo menos 8 caracteres.");
      }
      try {
        const { token, sessao: atual } = await gateway.login(emailLimpo, senha);
        tokens.definirToken(token, {
          manterConectado: opcoes?.manterConectado === true,
        });
        definirSessao(atual);
        definirErro(null);
        definirSemConexao(false);
        if (typeof window !== "undefined") {
          const limpo = window.location.hash.replace(/^#/, "").split("?")[0] ?? "";
          if (!limpo) {
            window.location.hash = atual.escopos?.includes("dashboard:ler")
              ? "dashboard"
              : "referencias";
          }
        }
      } catch (causa) {
        tokens.definirToken(null);
        definirSessao(null);
        if (causa instanceof ErroDaApi && (causa.ehNaoAutorizado || causa.status === 401)) {
          throw new Error("E-mail ou senha inválidos.");
        }
        if (causa instanceof ErroDaApi && causa.status === 403) {
          throw new Error("Usuário sem permissão de acesso.");
        }
        if (causa instanceof ErroDaApi && causa.status === 429) {
          throw new Error("Muitas tentativas. Aguarde 15 minutos.");
        }
        throw causa instanceof Error
          ? causa
          : new Error("Não foi possível entrar. Tente de novo.");
      }
    },
    [gateway, tokens],
  );

  const sair = useCallback(async () => {
    try {
      await gateway.logout();
    } catch {
      // Sessao ja invalidada no servidor ou rede: limpa local mesmo assim.
    }
    tokens.definirToken(null);
    definirSessao(null);
    definirErro(null);
    definirSemConexao(false);
  }, [gateway, tokens]);

  const trocarTenant = useCallback(
    async (tenantId: string) => {
      const { sessao: atual } = await gateway.trocarTenant(tenantId);
      definirSessao(atual);
      definirErro(null);
    },
    [gateway],
  );

  const valor = useMemo(
    () => ({
      sessao,
      carregando,
      erro,
      tokenAtual: tokens.tokenAtual,
      entrar,
      sair,
      trocarTenant,
      semConexao,
      tentarDeNovo: carregarSessao,
    }),
    [
      sessao,
      carregando,
      erro,
      tokens.tokenAtual,
      entrar,
      sair,
      trocarTenant,
      semConexao,
      carregarSessao,
    ],
  );

  return (
    <ContextoDeAuth.Provider value={valor}>{children}</ContextoDeAuth.Provider>
  );
}

export function useAuth(): EstadoDeAuth {
  const ctx = useContext(ContextoDeAuth);
  if (!ctx) {
    throw new Error("useAuth precisa estar dentro de ProvedorDeAuth.");
  }
  return ctx;
}
